import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Crown, TrendingUp, Zap, ShieldCheck } from "lucide-react";
import { api, slugify } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { BidWarsFeed } from "@/components/BidWarsFeed";
import { GlobalPodium } from "@/components/GlobalPodium";
import { GlobalDirectory } from "@/components/GlobalDirectory";
import { LiveTicker } from "@/components/LiveTicker";
import { HomeSeo } from "@/components/HomeSeo";
import { useRegion } from "@/context/RegionContext";

function TrendingCard({ board, index, onClick }) {
  return (
    <motion.button
      data-testid={`trending-card-${board.slug}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.07 }}
      onClick={onClick}
      className="glass rounded-2xl p-5 text-left hover:border-white/25 hover:-translate-y-1 transition-[transform,border-color] group"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 truncate">{board.name}</span>
        {board.verified && <ShieldCheck className="w-4 h-4 text-yellow-400 shrink-0" />}
      </div>
      {board.top ? (
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl overflow-hidden glass-strong flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{board.top.business_name}</div>
            <div className="text-xs text-white/40">{board.bidder_count} bidding</div>
          </div>
          <div className="font-display font-black text-emerald-400 tabular-nums">${board.top.max_daily_cap.toFixed(0)}</div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
          <Zap className="w-4 h-4" /> Unclaimed — grab #1
        </div>
      )}
    </motion.button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { setRegion } = useRegion();
  const [query, setQuery] = useState("");
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    document.title = "PodiumOne — Bid for #1";
    setRegion("global");
    api.get("/keywords/trending").then(({ data }) => setTrending(data)).catch(() => {});
  }, [setRegion]);

  const go = (e) => {
    e.preventDefault();
    const s = slugify(query);
    if (s) navigate(`/k/${s}`);
  };

  const examples = ["best coffee shops", "ai startups", "wedding photographers", "vegan restaurants"];

  return (
    <div>
      <Navbar />
      <section className="relative pt-40 pb-6 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-8 text-xs font-medium text-white/60">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> Real-time pay-to-rank leaderboards
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
            Own the <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-emerald-400 bg-clip-text text-transparent">#1 spot</span><br />for any keyword.
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="text-white/60 text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            Search a keyword, place a bid, and hold the top spot with a one-time Spot Price — from just <span className="text-white font-semibold">$5.00</span>.
          </motion.p>

          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} onSubmit={go} className="relative max-w-2xl mx-auto">
            <div className="glass-strong rounded-2xl h-20 flex items-center px-6 gap-4 focus-within:ring-2 focus-within:ring-blue-500/50 transition-all">
              <Search className="w-6 h-6 text-white/40 shrink-0" />
              <input
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search any keyword…"
                className="flex-1 bg-transparent outline-none text-xl sm:text-2xl font-medium placeholder:text-white/30"
              />
              <button data-testid="hero-search-btn" type="submit" className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 active:scale-95 transition-all h-12 px-5 font-semibold shrink-0">
                Search
              </button>
            </div>
          </motion.form>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
            <span className="text-xs text-white/30">Try:</span>
            {examples.map((ex) => (
              <button key={ex} data-testid={`example-${slugify(ex)}`} onClick={() => navigate(`/k/${slugify(ex)}`)} className="text-xs glass rounded-full px-3 py-1 text-white/60 hover:text-white hover:border-white/25 transition-colors">
                {ex}
              </button>
            ))}
          </div>
        </div>
      </section>

      <GlobalPodium />

      <section className="max-w-6xl mx-auto px-4 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <h2 className="font-display font-bold text-xl tracking-tight">Trending boards</h2>
            </div>
            {trending.length === 0 ? (
              <div className="glass rounded-2xl p-10 text-center text-white/40">No boards yet — search a keyword to create the first one.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {trending.map((b, i) => (
                  <TrendingCard key={b.slug} board={b} index={i} onClick={() => navigate(`/k/${b.slug}`)} />
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-1">
            <BidWarsFeed />
          </div>
        </div>
      </section>

      <HomeSeo ogUrl="https://podiumone.to" />
      <LiveTicker region="global" />
      <GlobalDirectory region="global" />
    </div>
  );
}
